/**
 * Google for Jobs `JobPosting` markup. Locked in by plan/v4 decision #9.
 *
 * Google drops a posting from the Jobs experience when a required field is
 * missing, so every required field is either present or the whole graph is
 * skipped. Nothing here is invented: a field the ingest could not extract is
 * omitted rather than guessed.
 */
import { canonicalUrl } from "./seo";
import { ORGANIZATION_ID } from "./schema";
import { getCountryName } from "./format";

const EMPLOYMENT_TYPE: Record<string, string> = {
  full_time: "FULL_TIME",
  part_time: "PART_TIME",
  contract: "CONTRACTOR",
  internship: "INTERN",
  temporary: "TEMPORARY",
};

const SALARY_UNIT: Record<string, string> = {
  hour: "HOUR",
  day: "DAY",
  month: "MONTH",
  year: "YEAR",
};

/** Google de-lists a posting whose validThrough has passed. Ingest rarely knows
 *  a real closing date, so an unexpired row gets a 90-day window from its
 *  posting date — long enough not to truncate live roles, short enough that a
 *  posting we stop refreshing ages out of Google instead of lingering. */
const DEFAULT_VALID_DAYS = 90;

type JobRow = {
  id: string;
  slug: string;
  title: string;
  company: string | null;
  applyUrl: string;
  employmentType: string | null;
  workMode: string | null;
  country: string | null;
  locationPrimary: string | null;
  compMin: number | null;
  compMax: number | null;
  compCurrency: string | null;
  compPeriod: string | null;
  yearsMin: number | null;
  postedAt: Date | string | number | null;
  firstSeenAt: Date | string | number | null;
  rawText?: string | null;
};

type Intel = {
  summary?: string[];
  responsibilities?: string[];
  mustHave?: Array<{ name: string }>;
  niceToHave?: Array<{ name: string }>;
} | null;

function toIso(value: Date | string | number | null | undefined) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function escapeHtml(s: string) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function list(items: string[]) {
  return `<ul>${items.map((i) => `<li>${escapeHtml(i)}</li>`).join("")}</ul>`;
}

/** Google requires a full HTML description. Built from the same fields the page
 *  renders, so the markup never describes something the visitor cannot see. */
export function jobDescriptionHtml(job: JobRow, intel: Intel) {
  const parts: string[] = [];
  if (intel?.summary?.length) {
    parts.push(intel.summary.map((s) => `<p>${escapeHtml(s)}</p>`).join(""));
  }
  if (intel?.responsibilities?.length) {
    parts.push(`<h3>Responsibilities</h3>${list(intel.responsibilities)}`);
  }
  if (intel?.mustHave?.length) {
    parts.push(`<h3>Required skills</h3>${list(intel.mustHave.map((s) => s.name))}`);
  }
  if (intel?.niceToHave?.length) {
    parts.push(`<h3>Nice to have</h3>${list(intel.niceToHave.map((s) => s.name))}`);
  }
  if (parts.length === 0 && job.rawText) {
    parts.push(`<p>${escapeHtml(job.rawText.slice(0, 5000))}</p>`);
  }
  return parts.join("");
}

/**
 * Checks if a location string indicates a purely remote role without a physical office.
 */
function isPureRemoteString(str?: string | null): boolean {
  if (!str) return true;
  const s = str.toLowerCase().trim();
  return (
    s === "remote" ||
    s.startsWith("remote,") ||
    s.startsWith("remote -") ||
    s.startsWith("remote (") ||
    s.includes("remote") ||
    s === "worldwide" ||
    s === "global" ||
    s === "anywhere"
  );
}

/**
 * Resolves the applicant country requirement for a remote role.
 */
function resolveRemoteCountry(countryCode?: string | null, location?: string | null): string | null {
  if (countryCode) {
    return getCountryName(countryCode) || countryCode;
  }
  if (!location) return null;
  const lower = location.toLowerCase();
  if (lower.includes("united states") || lower.includes("usa") || /\bus\b/i.test(location)) {
    return "United States";
  }
  if (lower.includes("india") || /\bin\b/i.test(location)) {
    return "India";
  }
  if (lower.includes("united kingdom") || lower.includes("uk") || /\bgb\b/i.test(location)) {
    return "United Kingdom";
  }
  if (lower.includes("canada") || /\bca\b/i.test(location)) {
    return "Canada";
  }
  if (lower.includes("germany") || /\bde\b/i.test(location)) {
    return "Germany";
  }
  return null;
}

/**
 * Parses a physical location string into PostalAddress fields (locality, region, country).
 */
function parsePostalAddress(locationPrimary: string | null, countryCode: string | null) {
  const countryName = countryCode ? getCountryName(countryCode) || countryCode : undefined;
  if (!locationPrimary) {
    return countryName ? { addressCountry: countryName } : null;
  }

  // Remove common prefixes
  const cleaned = locationPrimary.replace(/^(onsite|hybrid|remote)\s*[-:]\s*/i, "").trim();
  const parts = cleaned.split(",").map((p) => p.trim()).filter(Boolean);

  if (parts.length >= 3) {
    return {
      addressLocality: parts[0],
      addressRegion: parts[1],
      addressCountry: countryName || parts[parts.length - 1],
    };
  }

  if (parts.length === 2) {
    const p1Upper = parts[1].toUpperCase();
    const cUpper = countryCode?.toUpperCase();
    if (p1Upper === cUpper || p1Upper === "US" || p1Upper === "USA" || p1Upper === "UK" || p1Upper === "INDIA") {
      return {
        addressLocality: parts[0],
        addressCountry: countryName || parts[1],
      };
    }
    return {
      addressLocality: parts[0],
      addressRegion: parts[1],
      ...(countryName ? { addressCountry: countryName } : {}),
    };
  }

  if (parts.length === 1) {
    return {
      addressLocality: parts[0],
      ...(countryName ? { addressCountry: countryName } : {}),
    };
  }

  return countryName ? { addressCountry: countryName } : null;
}

export function jobPostingSchema(job: JobRow, intel: Intel) {
  const datePosted = toIso(job.postedAt) ?? toIso(job.firstSeenAt);
  const description = jobDescriptionHtml(job, intel);

  // Required by Google: title, description, datePosted, hiringOrganization,
  // and a location (physical or TELECOMMUTE). Bail rather than publish an
  // invalid graph that earns a Search Console error.
  if (!datePosted || !description || !job.company) return null;

  const validThrough = new Date(
    new Date(datePosted).getTime() + DEFAULT_VALID_DAYS * 86_400_000,
  ).toISOString();

  const isRemote = job.workMode === "remote";

  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job.title,
    description,
    datePosted,
    validThrough,
    identifier: {
      "@type": "PropertyValue",
      name: job.company,
      value: job.id,
    },
    hiringOrganization: {
      "@type": "Organization",
      name: job.company,
      sameAs: safeOrigin(job.applyUrl),
    },
    url: canonicalUrl(`/jobs/${job.slug}`),
    directApply: false,
    industry: "Information Technology",
    ...(job.employmentType && EMPLOYMENT_TYPE[job.employmentType]
      ? { employmentType: EMPLOYMENT_TYPE[job.employmentType] }
      : {}),
  };

  if (isRemote) {
    schema.jobLocationType = "TELECOMMUTE";
    const remoteCountry = resolveRemoteCountry(job.country, job.locationPrimary);
    if (remoteCountry) {
      schema.applicantLocationRequirements = {
        "@type": "Country",
        name: remoteCountry,
      };
    }
  }

  // Google documentation for Jobs:
  // Fully remote jobs should omit `jobLocation` (fixed physical addresses) to avoid
  // triggering non-critical warnings for missing streetAddress, postalCode, and addressRegion.
  // Only emit `jobLocation` when there is a real physical hub/office or hybrid requirement.
  const isPureRemoteRole = isRemote && isPureRemoteString(job.locationPrimary);

  if (!isPureRemoteRole) {
    const postalAddress = parsePostalAddress(job.locationPrimary, job.country);
    if (postalAddress) {
      schema.jobLocation = {
        "@type": "Place",
        address: {
          "@type": "PostalAddress",
          ...postalAddress,
        },
      };
    }
  }

  if (job.compMin != null && job.compCurrency) {
    schema.baseSalary = {
      "@type": "MonetaryAmount",
      currency: job.compCurrency,
      value: {
        "@type": "QuantitativeValue",
        minValue: job.compMin,
        ...(job.compMax != null ? { maxValue: job.compMax } : {}),
        unitText: SALARY_UNIT[job.compPeriod ?? "year"] ?? "YEAR",
      },
    };
  }

  if (job.yearsMin != null && job.yearsMin > 0) {
    schema.experienceRequirements = {
      "@type": "OccupationalExperienceRequirements",
      monthsOfExperience: job.yearsMin * 12,
    };
  }

  return schema;
}

/** The posting page itself, credited to JobCues rather than the employer. */
export function jobWebPageSchema(job: JobRow) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url: canonicalUrl(`/jobs/${job.slug}`),
    name: `${job.title}${job.company ? ` at ${job.company}` : ""}`,
    publisher: { "@id": ORGANIZATION_ID },
    dateModified: toIso(job.postedAt) ?? undefined,
  };
}

function safeOrigin(url: string) {
  try {
    return new URL(url).origin;
  } catch {
    return undefined;
  }
}
